import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Bell, Plus, Trash2, Edit, Camera, History, Car, Clock, MapPin, Search, Users, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchPlacasInteresse,
  createPlacaInteresse,
  updatePlacaInteresse,
  deletePlacaInteresse,
  fetchPessoasInteresse,
  createPessoaInteresse,
  updatePessoaInteresse,
  deletePessoaInteresse,
  authFetch,
  API_BASE_URL
} from '../lib/authFetch';

type EditFields = {
  placa?: string;
  descricao?: string;
  nome?: string;
};

const AlertsPanel = () => {
  const [newAlertType, setNewAlertType] = useState('vehicle');
  const [newAlertQuery, setNewAlertQuery] = useState('');
  const [placasInteresse, setPlacasInteresse] = useState<any[]>([]);
  const [pessoasInteresse, setPessoasInteresse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editPlaca, setEditPlaca] = useState<any>(null);
  const [editPessoa, setEditPessoa] = useState<any>(null);
  const [editFields, setEditFields] = useState<EditFields>({ placa: '', descricao: '', nome: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fotoPessoa, setFotoPessoa] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ tipo: 'placa' | 'pessoa', id: number } | null>(null);
  const [historyModal, setHistoryModal] = useState<{ tipo: 'placa' | 'pessoa', id: number, placa?: string, nome?: string } | null>(null);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [placas, pessoas] = await Promise.all([
        fetchPlacasInteresse(),
        fetchPessoasInteresse()
      ]);
      setPlacasInteresse(placas);
      setPessoasInteresse(pessoas);
    } catch {
      // Erro ao carregar dados
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFotoPessoa(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateAlert = async () => {
    if (newAlertType === 'vehicle' && !newAlertQuery.trim()) return;
    if (newAlertType === 'face' && !fotoPessoa) return;
    
    setLoading(true);
    try {
      if (newAlertType === 'vehicle') {
        await createPlacaInteresse({ placa: newAlertQuery.toUpperCase() });
        toast.success('Alerta de placa criado com sucesso!');
      } else if (newAlertType === 'face') {
        await createPessoaInteresse({ foto_url: fotoPessoa });
        toast.success('Alerta de pessoa criado com sucesso!');
      }
      await loadData();
      setNewAlertQuery('');
      setFotoPessoa(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      toast.error('Erro ao criar alerta');
    } finally {
      setLoading(false);
    }
  };

  const openEditPlaca = (alert: any) => {
    setEditPlaca(alert);
    setEditFields({ placa: alert.placa, descricao: alert.descricao || '' });
  };

  const openEditPessoa = (alert: any) => {
    setEditPessoa(alert);
    setEditFields({ nome: alert.nome || '', descricao: alert.descricao || '' });
  };

  const closeEdit = () => {
    setEditPlaca(null);
    setEditPessoa(null);
    setEditFields({});
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditFields({ ...editFields, [e.target.name]: e.target.value });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      if (editPlaca) {
        await updatePlacaInteresse(editPlaca.id, editFields);
        toast.success('Alerta atualizado!');
      } else if (editPessoa) {
        await updatePessoaInteresse(editPessoa.id, editFields);
        toast.success('Alerta atualizado!');
      }
      await loadData();
      closeEdit();
    } catch (e) {
      toast.error('Erro ao atualizar alerta');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleStatusPlaca = async (alert: any) => {
    try {
      await updatePlacaInteresse(alert.id, {
        ...alert,
        status: alert.status === 'ativo' ? 'inativo' : 'ativo'
      });
      await loadData();
      toast.success(alert.status === 'ativo' ? 'Alerta pausado' : 'Alerta ativado');
    } catch (e) {
      toast.error('Erro ao alterar status');
    }
  };

  const toggleStatusPessoa = async (alert: any) => {
    try {
      await updatePessoaInteresse(alert.id, {
        ...alert,
        status: alert.status === 'ativo' ? 'inativo' : 'ativo'
      });
      await loadData();
      toast.success(alert.status === 'ativo' ? 'Alerta pausado' : 'Alerta ativado');
    } catch (e) {
      toast.error('Erro ao alterar status');
    }
  };

  const openDeletePlaca = (id: number) => setDeleteModal({ tipo: 'placa', id });
  const openDeletePessoa = (id: number) => setDeleteModal({ tipo: 'pessoa', id });
  const closeDeleteModal = () => setDeleteModal(null);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setLoading(true);
    try {
      if (deleteModal.tipo === 'placa') {
        await deletePlacaInteresse(deleteModal.id);
      } else {
        await deletePessoaInteresse(deleteModal.id);
      }
      toast.success('Alerta excluído!');
      await loadData();
      closeDeleteModal();
    } catch (e) {
      toast.error('Erro ao excluir alerta');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricoPlaca = async (alert: any) => {
    setLoadingHistory(true);
    setHistoryModal({ tipo: 'placa', id: alert.id, placa: alert.placa });
    try {
      const params = new URLSearchParams();
      params.append('placa', alert.placa);
      const response = await authFetch(`${API_BASE_URL}/detecoes_camera?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setHistoryList(data.map((item: any) => ({
          data: new Date(item.data_hora).toLocaleString('pt-BR'),
          local: item.localizacao || '-',
          img: item.imagem_url || '/placeholder.svg'
        })));
      } else {
        setHistoryList([]);
      }
    } catch (e) {
      setHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchHistoricoPessoa = async (alert: any) => {
    setLoadingHistory(true);
    setHistoryModal({ tipo: 'pessoa', id: alert.id, nome: alert.nome });
    try {
      const params = new URLSearchParams();
      params.append('pessoa_interesse_id', String(alert.id));
      const response = await authFetch(`${API_BASE_URL}/detecoes_facial?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setHistoryList(data.map((item: any) => ({
          data: new Date(item.data_hora).toLocaleString('pt-BR'),
          local: item.localizacao || '-',
          img: item.imagem_base64
            ? (item.imagem_base64.startsWith('data:') ? item.imagem_base64 : `data:image/jpeg;base64,${item.imagem_base64}`)
            : item.imagem_url?.startsWith('data:') ? item.imagem_url : '/placeholder.svg'
        })));
      } else {
        setHistoryList([]);
      }
    } catch (e) {
      setHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const closeHistoryModal = () => { 
    setHistoryModal(null); 
    setHistoryList([]); 
  };

  const totalAlertas = placasInteresse.length + pessoasInteresse.length;
  const alertasAtivos =
    placasInteresse.filter(a => a.status === 'ativo').length +
    pessoasInteresse.filter(a => a.status === 'ativo').length;

  const pessoaFotoSrc = (alert: { foto_base64?: string; foto_url?: string }) => {
    if (alert.foto_base64) {
      return alert.foto_base64.startsWith('data:')
        ? alert.foto_base64
        : `data:image/jpeg;base64,${alert.foto_base64}`;
    }
    if (alert.foto_url?.startsWith('data:')) return alert.foto_url;
    return undefined;
  };

  return (
    <div className="space-y-6">
      {/* Criar Novo Alerta */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Cadastrar Alerta de Interesse
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Monitore placas de veículos ou pessoas nas câmeras
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <Button
              type="button"
              variant="outline"
              className={
                newAlertType === 'vehicle'
                  ? 'bg-protec-gold text-black border-protec-gold hover:bg-protec-gold hover:text-black'
                  : 'bg-protec-dark border-protec-gold/40 text-gray-300 hover:bg-protec-dark-light hover:text-white'
              }
              onClick={() => setNewAlertType('vehicle')}
            >
              <Car className="w-4 h-4 mr-2" />
              Placa
            </Button>
            <Button
              type="button"
              variant="outline"
              className={
                newAlertType === 'face'
                  ? 'bg-protec-gold text-black border-protec-gold hover:bg-protec-gold hover:text-black'
                  : 'bg-protec-dark border-protec-gold/40 text-gray-300 hover:bg-protec-dark-light hover:text-white'
              }
              onClick={() => setNewAlertType('face')}
            >
              <Users className="w-4 h-4 mr-2" />
              Pessoa
            </Button>
          </div>

          {newAlertType === 'vehicle' ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 max-w-md">
              <Input
                value={newAlertQuery}
                onChange={(e) => setNewAlertQuery(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7))}
                placeholder="Digite a placa..."
                className="bg-protec-dark border-protec-gold/30 text-white"
                maxLength={7}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateAlert()}
              />
              <Button
                onClick={handleCreateAlert}
                disabled={loading || !newAlertQuery.trim()}
                className="bg-protec-gold text-black hover:shadow-gold px-4 sm:px-6 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-w-lg">
              <p className="text-xs text-gray-500">
                Envie uma foto com rosto visível. O sistema gera a biometria e alerta quando detectar nas câmeras.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <div
                  className="w-full sm:w-40 h-32 border-2 border-dashed border-protec-gold/30 rounded-lg flex items-center justify-center cursor-pointer hover:border-protec-gold/50 overflow-hidden"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {fotoPessoa ? (
                    <img src={fotoPessoa} alt="Foto" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-gray-400 p-2">
                      <Upload className="w-8 h-8 mx-auto mb-1 text-protec-gold" />
                      <span className="text-xs">Selecionar foto</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  onClick={handleCreateAlert}
                  disabled={loading || !fotoPessoa}
                  className="bg-protec-gold text-black hover:shadow-gold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Cadastrar pessoa
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Indicadores */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-protec-dark-light border-protec-gold/20">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Total</p>
                <p className="text-lg sm:text-xl font-bold text-white">{totalAlertas}</p>
              </div>
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-protec-dark-light border-protec-gold/20">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Ativos</p>
                <p className="text-lg sm:text-xl font-bold text-white">{alertasAtivos}</p>
              </div>
              <Badge className="bg-green-500/20 text-green-400 text-[10px] sm:text-xs hidden sm:flex">Monitorando</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-protec-dark-light border-protec-gold/20">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
              <div>
                <p className="text-xs sm:text-sm text-gray-400">Pessoas</p>
                <p className="text-lg sm:text-xl font-bold text-white">{pessoasInteresse.length}</p>
              </div>
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold hidden sm:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Alertas */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Alertas Cadastrados ({totalAlertas})
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Gerencie seus alertas de veículos e pessoas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-6 sm:py-8 text-gray-400 text-sm">Carregando...</div>
          ) : totalAlertas === 0 ? (
            <div className="text-center py-6 sm:py-8">
              <Bell className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-400 text-sm sm:text-base">Nenhum alerta cadastrado</p>
              <p className="text-gray-500 text-xs sm:text-sm">Crie um alerta acima para começar a monitorar</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {/* Placas de Interesse */}
              {placasInteresse.map((alert) => (
                <div key={`placa-${alert.id}`} className="bg-protec-dark rounded-lg p-3 sm:p-4 border border-protec-gold/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                    {/* Ícone/Imagem */}
                    <div className="relative">
                      <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                        <Car className="w-8 h-8 sm:w-12 sm:h-12 text-protec-gold" />
                      </div>
                      <Badge className={`absolute top-1 sm:top-2 right-1 sm:right-2 text-[10px] sm:text-xs ${alert.status === 'ativo' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {alert.status === 'ativo' ? 'Ativo' : 'Pausado'}
                      </Badge>
                    </div>
                    
                    {/* Detalhes */}
                    <div className="md:col-span-2 space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base sm:text-lg font-semibold text-white">{alert.placa}</h3>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={alert.status === 'ativo'} 
                            onCheckedChange={() => toggleStatusPlaca(alert)} 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <p className="text-gray-400">Tipo</p>
                          <p className="text-white">Veículo</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Cadastrado</p>
                          <p className="text-white">{new Date(alert.data_cadastro).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-400">Descrição</p>
                          <p className="text-white truncate">{alert.descricao || 'Sem descrição'}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 pt-1 sm:pt-2">
                        <Button 
                          size="sm" 
                          onClick={() => openEditPlaca(alert)}
                          className="bg-protec-gold/10 text-protec-gold hover:bg-protec-gold hover:text-black text-xs h-7 sm:h-8 px-2 sm:px-3"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => fetchHistoricoPlaca(alert)}
                          className="bg-protec-gold/10 text-protec-gold hover:bg-protec-gold hover:text-black text-xs h-7 sm:h-8 px-2 sm:px-3"
                        >
                          <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="hidden xs:inline">Histórico</span>
                          <span className="xs:hidden">Hist.</span>
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => openDeletePlaca(alert.id)}
                          className="bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white text-xs h-7 sm:h-8 px-2 sm:px-3"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Excluir</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pessoas de Interesse */}
              {pessoasInteresse.map((alert) => {
                const src = pessoaFotoSrc(alert);
                return (
                  <div key={`pessoa-${alert.id}`} className="bg-protec-dark rounded-lg p-3 sm:p-4 border border-purple-500/20">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                      <div className="relative">
                        {src ? (
                          <img src={src} alt="" className="aspect-video w-full object-cover rounded-lg border border-protec-gold/20" />
                        ) : (
                          <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                            <Users className="w-8 h-8 sm:w-12 sm:h-12 text-purple-400" />
                          </div>
                        )}
                        <Badge className={`absolute top-1 sm:top-2 right-1 sm:right-2 text-[10px] sm:text-xs ${alert.status === 'ativo' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {alert.status === 'ativo' ? 'Ativo' : 'Pausado'}
                        </Badge>
                      </div>
                      <div className="md:col-span-2 space-y-2 sm:space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base sm:text-lg font-semibold text-white">{alert.nome || `Pessoa #${alert.id}`}</h3>
                          <Switch checked={alert.status === 'ativo'} onCheckedChange={() => toggleStatusPessoa(alert)} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                          <div>
                            <p className="text-gray-400">Tipo</p>
                            <p className="text-white">Facial / Watchlist</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Cadastrado</p>
                            <p className="text-white">{new Date(alert.data_cadastro).toLocaleDateString('pt-BR')}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-400">Descrição</p>
                            <p className="text-white truncate">{alert.descricao || 'Sem descrição'}</p>
                          </div>
                          {alert.embedding_cadastrado !== undefined && (
                            <div className="col-span-2">
                              <Badge className={alert.embedding_cadastrado ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                {alert.embedding_cadastrado ? 'Biometria ativa' : 'Biometria pendente'}
                              </Badge>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1 sm:pt-2">
                          <Button size="sm" onClick={() => openEditPessoa(alert)} className="bg-protec-gold/10 text-protec-gold hover:bg-protec-gold hover:text-black text-xs h-7 sm:h-8 px-2 sm:px-3">
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            Editar
                          </Button>
                          <Button size="sm" onClick={() => fetchHistoricoPessoa(alert)} className="bg-protec-gold/10 text-protec-gold hover:bg-protec-gold hover:text-black text-xs h-7 sm:h-8 px-2 sm:px-3">
                            <History className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            Histórico
                          </Button>
                          <Button size="sm" onClick={() => openDeletePessoa(alert.id)} className="bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white text-xs h-7 sm:h-8 px-2 sm:px-3">
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Edição */}
      <Dialog open={!!editPlaca || !!editPessoa} onOpenChange={closeEdit}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20">
          <DialogHeader>
            <DialogTitle className="text-white">
              Editar {editPlaca ? 'Alerta de Placa' : 'Alerta de Pessoa'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editPlaca && (
              <>
                <div>
                  <Label className="text-gray-300">Placa</Label>
                  <Input 
                    name="placa" 
                    value={editFields.placa || ''} 
                    onChange={handleEditChange}
                    className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Descrição</Label>
                  <Input 
                    name="descricao" 
                    value={editFields.descricao || ''} 
                    onChange={handleEditChange}
                    className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                    placeholder="Ex: Veículo suspeito, caso #123"
                  />
                </div>
              </>
            )}
            {editPessoa && (
              <>
                <div>
                  <Label className="text-gray-300">Nome</Label>
                  <Input 
                    name="nome" 
                    value={editFields.nome || ''} 
                    onChange={handleEditChange}
                    className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Descrição</Label>
                  <Input 
                    name="descricao" 
                    value={editFields.descricao || ''} 
                    onChange={handleEditChange}
                    className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                    placeholder="Ex: Pessoa procurada, caso #456"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-protec-gold text-black">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={!!deleteModal} onOpenChange={closeDeleteModal}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-gray-300">
            Tem certeza que deseja excluir este alerta? Esta ação não pode ser desfeita.
          </div>
          <DialogFooter>
            <Button onClick={confirmDelete} disabled={loading} className="bg-red-500 text-white hover:bg-red-600">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Histórico */}
      <Dialog open={!!historyModal} onOpenChange={closeHistoryModal}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Histórico de Detecções - {historyModal?.placa || historyModal?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {loadingHistory ? (
              <div className="text-center text-gray-400">Carregando...</div>
            ) : historyList.length === 0 ? (
              <div className="text-center py-8">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Nenhuma detecção registrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historyList.map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-center bg-protec-dark rounded-lg p-3 border border-protec-gold/10">
                    <div className="w-20 h-14 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                      <img src={item.img} alt="Câmera" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-white">
                        <Clock className="w-4 h-4 text-protec-gold" />
                        {item.data}
                      </div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <MapPin className="w-4 h-4" />
                        {item.local}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={closeHistoryModal} className="bg-protec-gold text-black">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertsPanel;
